import { z } from 'zod';
import { AddCommentRequest, CreateUniformPrinterRequest, MoveStageRequest, MoveStageResponse, ProductionAttachmentSummary, ProductionOrderResponse, ProductionQueueResponse, ProductionSheetVersionResponse, ProductionTimelineEntrySchema, RepeatProductionOrderRequest, RepeatProductionOrderResponse, ReplaceGradeRequest, ReplaceMemberFunctionsRequest, ReplacePersonalizationRequest, ReplacePrintersRequest, SaveArtRequest, SaveArtResponse, UniformMemberListResponse, UniformFabricListResponse, UniformPrinterListResponse, UniformPrinterSummary, UpdateProductionSheetRequest, UpdateUniformPrinterRequest, UniformViewerResponse, } from '@aeris/contracts';
import { request } from "./client.js";
/**
 * `/api/v1/uniforms` — a fila de produção de uniformes (F170).
 *
 * Toda rota daqui responde 403 `feature_not_enabled` para a conta sem a chave
 * `uniformes`. A tela nem aparece nessas contas (`feature` em `MODULES`), então
 * a recusa só chega a quem colou o endereço.
 */
const queryString = (query) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
        if (typeof value === 'string' && value !== '')
            params.set(key, value);
        if (typeof value === 'number')
            params.set(key, String(value));
    }
    const text = params.toString();
    return text === '' ? '' : `?${text}`;
};
export const fetchProductionQueue = async (query, signal) => ProductionQueueResponse.parse(await request(`/api/v1/uniforms/orders${queryString(query)}`, { signal }));
export const fetchProductionOrder = async (id, signal) => ProductionOrderResponse.parse(await request(`/api/v1/uniforms/orders/${id}`, { signal }));
export const saveProductionSheet = async (id, input) => ProductionSheetVersionResponse.parse(await request(`/api/v1/uniforms/orders/${id}`, {
    method: 'PUT',
    body: UpdateProductionSheetRequest.parse(input),
})).version;
export const saveProductionGrade = async (id, input) => ProductionSheetVersionResponse.parse(await request(`/api/v1/uniforms/orders/${id}/grade`, {
    method: 'PUT',
    body: ReplaceGradeRequest.parse(input),
})).version;
export const saveProductionPersonalization = async (id, input) => ProductionSheetVersionResponse.parse(await request(`/api/v1/uniforms/orders/${id}/personalization`, {
    method: 'PUT',
    body: ReplacePersonalizationRequest.parse(input),
})).version;
export const moveProductionStage = async (id, input) => MoveStageResponse.parse(await request(`/api/v1/uniforms/orders/${id}/stage`, {
    method: 'POST',
    body: MoveStageRequest.parse(input),
}));
export const repeatProductionOrder = async (id, input) => RepeatProductionOrderResponse.parse(await request(`/api/v1/uniforms/orders/${id}/repeat`, {
    method: 'POST',
    body: RepeatProductionOrderRequest.parse(input),
}));
/**
 * Sobe uma imagem do pedido. Sem `Content-Type` escrito à mão: quem monta a
 * fronteira do multipart é o `FormData`.
 */
export const uploadProductionAttachment = async (id, file) => {
    const form = new FormData();
    form.append('file', file);
    return z.array(ProductionAttachmentSummary).parse(await request(`/api/v1/uniforms/orders/${id}/attachments`, {
        method: 'POST',
        body: form,
    }));
};
export const deleteProductionAttachment = async (id, attachmentId) => {
    await request(`/api/v1/uniforms/orders/${id}/attachments/${attachmentId}`, {
        method: 'DELETE',
    });
};
/** Os tecidos da loja (F260): a ficha e o filtro da fila escolhem entre eles. */
export const fetchUniformFabrics = async (signal) => UniformFabricListResponse.parse(await request('/api/v1/uniforms/fabrics', { signal })).fabrics;
export const fetchUniformPrinters = async (status, signal) => UniformPrinterListResponse.parse(await request(`/api/v1/uniforms/printers${status === undefined ? '' : `?status=${status}`}`, { signal })).printers;
export const createUniformPrinter = async (input) => UniformPrinterSummary.parse(await request('/api/v1/uniforms/printers', {
    method: 'POST',
    body: CreateUniformPrinterRequest.parse(input),
}));
export const updateUniformPrinter = async (id, input) => UniformPrinterSummary.parse(await request(`/api/v1/uniforms/printers/${id}`, {
    method: 'PATCH',
    body: UpdateUniformPrinterRequest.parse(input),
}));
// ---------------------------------------------------------------------------
// F171 — a arte, as impressoras, o comentário, a equipe
// ---------------------------------------------------------------------------
export const saveProductionArt = async (id, input) => SaveArtResponse.parse(await request(`/api/v1/uniforms/orders/${id}/art`, {
    method: 'PUT',
    body: SaveArtRequest.parse(input),
}));
export const replaceProductionPrinters = async (id, input) => {
    await request(`/api/v1/uniforms/orders/${id}/printers`, {
        method: 'PUT',
        body: ReplacePrintersRequest.parse(input),
    });
};
export const addProductionComment = async (id, input) => ProductionTimelineEntrySchema.parse(await request(`/api/v1/uniforms/orders/${id}/comments`, {
    method: 'POST',
    body: AddCommentRequest.parse(input),
}));
/** Quem está olhando: se gerencia a produção e quais funções tem. */
export const fetchUniformViewer = async (signal) => UniformViewerResponse.parse(await request('/api/v1/uniforms/me', { signal }));
export const fetchUniformMembers = async (signal) => UniformMemberListResponse.parse(await request('/api/v1/uniforms/members', { signal })).members;
export const replaceUniformMemberFunctions = async (userId, input) => {
    await request(`/api/v1/uniforms/members/${userId}/functions`, {
        method: 'PUT',
        body: ReplaceMemberFunctionsRequest.parse(input),
    });
};
//# sourceMappingURL=uniforms.js.map